import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import {
    Container,
    Typography,
    Box,
    TextField,
    Button,
    Grid,
    MenuItem,
} from '@mui/material';

function InstructionForm() {
    const { sessionId, exerciseId, instructionId } = useParams();
    const navigate = useNavigate();
    const [instruction, setInstruction] = useState({
        type: 'moteur',
        text_affichage: '',
        texte_vocal: '',
        video_url: '',
        image: ''
    });

    useEffect(() => {
        if (instructionId) {
            const fetchInstruction = async () => {
                try {
                    const response = await apiClient.get(`/sessions/${sessionId}/exercises/${exerciseId}/instructions/${instructionId}`);
                    setInstruction(response.data);
                } catch (error) {
                    console.error('Erreur lors de la récupération de l\'instruction:', error);
                }
            };
            fetchInstruction();
        }
    }, [sessionId, exerciseId, instructionId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInstruction(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Fonction pour vérifier si l'URL est une vidéo valide
    const isValidVideoUrl = (url) => {
        if (!url || typeof url !== 'string') return false;
        
        const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.m4v'];
        const lowerUrl = url.toLowerCase();
        
        // Vérifier l'extension
        const hasValidExtension = videoExtensions.some(ext => lowerUrl.includes(ext));
        
        // Vérifier si c'est une URL valide
        const isValidUrl = url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:video/');
        
        return hasValidExtension && isValidUrl;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (instructionId) {
                await apiClient.put(`/sessions/${sessionId}/exercises/${exerciseId}/instructions/${instructionId}`, instruction);
            } else {
                await apiClient.post(`/sessions/${sessionId}/exercises/${exerciseId}/instructions`, instruction);
            }
            navigate(`/sessions/${sessionId}`);
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de l\'instruction:', error);
        }
    };

    return (
        <Container maxWidth="md">
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {instructionId ? 'Modifier l\'instruction' : 'Nouvelle instruction'}
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            select
                            label="Type d'instruction"
                            name="type"
                            value={instruction.type}
                            onChange={handleChange}
                            required
                        >
                            <MenuItem value="moteur">Motrice</MenuItem>
                            <MenuItem value="cognitif">Cognitive</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Texte d'affichage"
                            name="text_affichage"
                            value={instruction.text_affichage}
                            onChange={handleChange}
                            required
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Texte vocal"
                            name="texte_vocal"
                            value={instruction.texte_vocal}
                            onChange={handleChange}
                            multiline
                            rows={2}
                        />
                    </Grid>

                    {instruction.type === 'moteur' && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="URL de la vidéo"
                                name="video_url"
                                value={instruction.video_url}
                                onChange={handleChange}
                                sx={{ mb: 2 }}
                            />
                            {/* Debug info */}
                            <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Debug: video_url = "{instruction.video_url}" | 
                                    Type: {instruction.type} | 
                                    URL valide: {String(isValidVideoUrl(instruction.video_url))} |
                                    Condition: {String(instruction.video_url && instruction.video_url.trim() !== '')}
                                </Typography>
                            </Box>
                            {/* Lecteur vidéo avec vérification d'extension */}
                            {instruction.video_url && instruction.video_url.trim() !== '' && (
                                <Box sx={{ mt: 2, width: '100%' }}>
                                    {isValidVideoUrl(instruction.video_url) ? (
                                        <Box sx={{ p: 2, border: '2px solid green', borderRadius: 1 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'green' }}>
                                                ✅ Aperçu de la vidéo (URL valide)
                                            </Typography>
                                            <video 
                                                controls 
                                                width="100%" 
                                                style={{ maxWidth: '500px', borderRadius: '8px', border: '1px solid #ccc' }}
                                                preload="metadata"
                                                onError={(e) => {
                                                    console.error('Erreur de chargement vidéo:', e);
                                                    console.error('URL:', instruction.video_url);
                                                }}
                                                onLoadStart={() => {
                                                    console.log('Début du chargement vidéo:', instruction.video_url);
                                                }}
                                                onCanPlay={() => {
                                                    console.log('Vidéo prête à être lue:', instruction.video_url);
                                                }}
                                            >
                                                <source src={instruction.video_url} type="video/mp4" />
                                                <source src={instruction.video_url} type="video/webm" />
                                                <source src={instruction.video_url} type="video/ogg" />
                                                <p>Votre navigateur ne supporte pas la lecture de vidéos.</p>
                                                <p>URL: {instruction.video_url}</p>
                                            </video>
                                        </Box>
                                    ) : (
                                        <Box sx={{ p: 2, border: '2px solid orange', borderRadius: 1 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'orange' }}>
                                                ⚠️ URL de vidéo invalide
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                L'URL ne semble pas pointer vers un fichier vidéo valide.
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Extensions supportées: .mp4, .webm, .ogg, .avi, .mov, .wmv, .flv, .mkv, .m4v
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                URL actuelle: {instruction.video_url}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Grid>
                    )}

                    {instruction.type === 'cognitif' && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="URL de l'image"
                                name="image"
                                value={instruction.image}
                                onChange={handleChange}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(`/sessions/${sessionId}`)}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                            >
                                {instructionId ? 'Mettre à jour' : 'Créer l\'instruction'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
}

export default InstructionForm; 